import { useEffect, useMemo, useState } from "react";
import { services } from "../../services/api";
import { getObjLC } from "../../utils/getObjLC";
import { useParams } from "react-router-dom";

const ALLOWED_CAT = [
  { id: "GR04", tramite: "educacion" },
  { id: "GR06", tramite: "protec-civil" },
  { id: "GR17", tramite: "sedeco" },
];

export const usePagosMultiples = () => {
  const { tramite, id } = useParams();
  const [errorBackend, setErrorBackend] = useState(null);
  const [loader, setLoader] = useState(false);
  const [loaderGenLC, setLoaderGenLC] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [addedConcepts, setAddedConcepts] = useState([]);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState({ title: "", data: [], resGenLC: {} });
  const [dataLC, setDataLC] = useState({
    rfc: "",
    domicilio: "",
    nombre: "",
    apaterno: "",
    amaterno: "",
    observaciones: "",
    tp_persona: "fisica",
  });
  const getURL = `categorias/2/${id}`;
  const genLCURL = `v2/generaLineaCaptura/${id}`;

  useEffect(() => {
    const isValidCombination = ALLOWED_CAT.some(
      (cat) => cat.id === id && cat.tramite === tramite,
    );
    if (!isValidCombination) {
      setState((prev) => ({ ...prev, title: "", data: [] }));
      return;
    }
    const getData = async () => {
      setLoader(true);
      try {
        const response = await services("GET", getURL);

        const conceptosRaw = Array.isArray(response?.data?.TB_CONCEPTOS)
          ? response.data.TB_CONCEPTOS
          : [];

        const title =
          conceptosRaw.length > 0 ? conceptosRaw[0].DESC_CATEGORIA : "";

        const conceptosConId = conceptosRaw.map((concepto, index) => ({
          ...concepto,
          internalId: crypto.randomUUID(),
          CANTIDAD: 1,
          TP_DIVERSO: response?.data?.TP_DIVERSO,
          show: true,
          loading: false,
        }));

        setState((prev) => ({ ...prev, title, data: conceptosConId }));
      } catch (error) {
        setState((prev) => ({ ...prev, title: "", data: [] }));
      } finally {
        setLoader(false);
      }
    };
    getData();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredConcepts = useMemo(() => {
    const { data } = state;

    if (searchTerm.trim().length < 3) return data;

    const term = searchTerm.toLowerCase().trim();

    return data.filter((concept) =>
      concept?.TEXTO_CONCEPTO?.toLowerCase().includes(term),
    );
  }, [searchTerm, state.data]);

  const handleSearchTerm = (term) => {
    setSearchTerm(term);
    setPage(0);
  };

  const handleRemoveConcept = (item) => {
    setAddedConcepts((prev) =>
      prev.filter((concept) => concept.internalId !== item.internalId),
    );
    setState((prev) => ({
      ...prev,
      data: prev.data.map((concept) =>
        concept.internalId === item.internalId
          ? { ...concept, show: true, CANTIDAD: 1 }
          : concept,
      ),
    }));
  };

  const handleUpdateQuantity = (internalId, action) => {
    setState((prev) => ({
      ...prev,
      data: prev.data.map((item) => {
        if (item.internalId === internalId) {
          const nextValue =
            action === "increment" ? item.CANTIDAD + 1 : item.CANTIDAD - 1;
          const validatedQuantity = Math.min(Math.max(1, nextValue), 1999);
          return {
            ...item,
            CANTIDAD: validatedQuantity,
          };
        }
        return item;
      }),
    }));
  };

  const formatter = new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
  });

  const handleConsultConcept = async (concept, setNotification) => {
    const { TP_DIVERSO, internalId } = concept;

    setState((prev) => ({
      ...prev,
      data: prev.data.map((item) =>
        item.internalId === internalId ? { ...item, loading: true } : item,
      ),
    }));

    const genData = {
      procesamiento: "1",
      rfc: "CONSULTA",
      nombre: "CONSULTA",
      diverso: TP_DIVERSO,
    };

    const objLC = getObjLC(genData, [concept]);

    try {
      const response = await services("POST", genLCURL, objLC);
      if (response?.status !== 200) {
        setNotification({
          open: true,
          message: "Hubo un error al consultar la información del concepto.",
          severity: "error",
        });
        setState((prev) => ({
          ...prev,
          data: prev.data.map((item) =>
            item.internalId === internalId ? { ...item, loading: false } : item,
          ),
        }));
        return;
      }

      const detalleCalculado = response?.data?.TB_CALC_DETALLE?.[0];

      if (detalleCalculado) {
        const conceptoConIdPreservado = {
          ...detalleCalculado,
          internalId: internalId,
        };

        setAddedConcepts((prev) => [...prev, conceptoConIdPreservado]);

        setNotification({
          open: true,
          message: "Concepto agregado con éxito",
          severity: "success",
        });

        setState((prev) => ({
          ...prev,
          data: prev.data.map((item) =>
            item.internalId === internalId
              ? { ...item, loading: false, show: false }
              : item,
          ),
        }));
      }
    } catch (error) {
      console.error("Error al consultar el concepto:", error);
      setState((prev) => ({
        ...prev,
        data: prev.data.map((item) =>
          item.internalId === internalId ? { ...item, loading: false } : item,
        ),
      }));

      if (setNotification) {
        setNotification({
          open: true,
          message: "Hubo un error al consultar la información del concepto.",
          severity: "error",
        });
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let finalValue = name === "rfc" ? value.toUpperCase() : value;

    if (name === "rfc") {
      finalValue = finalValue.replace(/[^A-Z0-9&]/g, "");

      const longitudMaxima = dataLC.tp_persona === "moral" ? 12 : 13;

      if (finalValue.length > longitudMaxima) {
        return;
      }
    }
    setDataLC((prev) => ({
      ...prev,
      [name]: finalValue,
    }));
  };

  const handleGenLC = async () => {
    const nuevosErrores = {};
    const {
      rfc,
      domicilio,
      nombre,
      apaterno,
      amaterno,
      observaciones,
      tp_persona,
    } = dataLC;

    if (!rfc?.trim()) nuevosErrores.rfc = "El RFC es obligatorio.";
    if (rfc?.trim().length !== 13 && tp_persona === "fisica" ) nuevosErrores.rfc = "El formato del RFC es inválido. Debe tener 13 (Persona Física) caracteres"
    if (rfc?.trim().length !== 12 && tp_persona === "moral" ) nuevosErrores.rfc = "El formato del RFC es inválido. Debe tener 12 (Persona Moral) caracteres"

    if (!domicilio?.trim()) nuevosErrores.domicilio = "La dirección es obligatoria.";
    if (!nombre?.trim()) nuevosErrores.nombre = "El nombre es obligatorio.";
    if (!apaterno?.trim() && tp_persona === "fisica") nuevosErrores.apaterno = "El apellido paterno es obligatorio.";

    if (Object.keys(nuevosErrores).length > 0) {
      setErrors(nuevosErrores);
      return;
    }

    setErrors({});

    const rfcLimpio = rfc.trim().toUpperCase();
    if (rfcLimpio.length < 12 || rfcLimpio.length > 13) {
      setErrorBackend(
        "El formato del RFC es inválido. Debe tener 12 (Persona Moral) o 13 (Persona Física) caracteres.",
      );
      return;
    }

    const { data } = state;

    const nombreCompleto =
      `${nombre.trim()} ${apaterno.trim()} ${amaterno?.trim() || ""}`.trim();

    const genData = {
      procesamiento: "2",
      diverso: data[0]?.TP_DIVERSO,
      rfc: rfcLimpio,
      nombre: nombreCompleto,
      domicilio: domicilio.trim(),
      observaciones: observaciones?.trim() || "",
    };

    const objLC = getObjLC(genData, addedConcepts);

    try {
      setLoaderGenLC(true);
      setErrorBackend(null);

      const response = await services("POST", genLCURL, objLC);

      if (response?.data?.created === false || response?.data?.error) {
        setErrorBackend(
          response?.data?.error || "Ocurrió un error al procesar la solicitud.",
        );
        return;
      }

      setState((prev) => ({ ...prev, resGenLC: response?.data }));
    } catch (error) {
      console.error("Error al generar la linea de captura:", error);
      setErrorBackend(
        "Error de comunicación con el servidor. Intente más tarde.",
      );
    } finally {
      setLoaderGenLC(false);
    }
  };

  const handleResetFlujo = () => {
    setAddedConcepts([]);
    setDataLC({
      rfc: "",
      domicilio: "",
      nombre: "",
      apaterno: "",
      amaterno: "",
      observaciones: "",
      tp_persona: "fisica",
    });
    setState((prevState) => ({
      ...prevState,
      resGenLC: {},
      data: prevState.data.map((concepto) => ({
        ...concepto,
        show: true,
        CANTIDAD: 1,
      })),
    }));
    if (typeof setLoaderGenLC === "function") setLoaderGenLC(false);
  };

  return {
    loader,
    state,
    searchTerm,
    handleSearchTerm,
    filteredConcepts,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    addedConcepts,
    handleConsultConcept,
    handleUpdateQuantity,
    formatter,
    handleRemoveConcept,
    handleInputChange,
    dataLC,
    handleGenLC,
    loaderGenLC,
    handleResetFlujo,
    errorBackend,
    setErrorBackend,
    errors,
    setDataLC,
  };
};
